import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  ReactNode,
} from "react";
import { authService, User } from "../services/authService";

interface AuthContextType {
  user: User | null;
  loading: boolean;
  isAuthenticated: boolean;
  login: () => Promise<void>;
  logout: () => Promise<void>;
  refreshUser: (force?: boolean) => Promise<void>;
}

export const AuthContext = createContext<AuthContextType | undefined>(
  undefined,
);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};

interface AuthProviderProps {
  children: ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState<boolean>(true);

  const loadUser = async (force = false) => {
    console.debug("[AuthContext] loadUser called", {
      force,
      timestamp: new Date().toISOString(),
    });
    // Helpful trace to find who invoked loadUser. Guard behind non-production
    // check to avoid noisy stack traces in production logs.
    if (process.env.NODE_ENV !== "production") {
      // Helpful trace to find who invoked loadUser. Only enabled outside
      // production to avoid noisy stack traces in logs.
      console.trace("[AuthContext] loadUser trace");
    }
    try {
      // Try to get from localStorage first
      const storedUser = localStorage.getItem("user");
      if (storedUser) {
        const parsedUser = JSON.parse(storedUser);
        console.debug("[AuthContext] Found stored user in localStorage", {
          userId: parsedUser.id,
          userEmail: parsedUser.email,
        });
        setUser(parsedUser);
      } else {
        console.debug("[AuthContext] No stored user in localStorage");
      }

      // Then verify with backend
      console.debug("[AuthContext] Calling authService.getCurrentUser", {
        force,
      });
      const currentUser = await authService.getCurrentUser(force);

      if (currentUser) {
        console.log("[AuthContext] Backend verified user is authenticated", {
          userId: currentUser.id,
          userEmail: currentUser.email,
          timestamp: new Date().toISOString(),
        });
        setUser(currentUser);
        localStorage.setItem("user", JSON.stringify(currentUser));
      } else {
        console.warn(
          "[AuthContext] Backend verification failed - user not authenticated",
        );
        setUser(null);
        localStorage.removeItem("user");
      }
    } catch (error) {
      console.error("[AuthContext] Error loading user:", error);
      setUser(null);
      localStorage.removeItem("user");
    } finally {
      setLoading(false);
      console.debug("[AuthContext] loadUser completed");
    }
  };

  useEffect(() => {
    loadUser();
  }, []);

  const login = async () => {
    // login will redirect the browser to the backend OAuth start endpoint
    await authService.login();
  };

  const logout = async () => {
    try {
      await authService.logout();
    } catch (err) {
      console.debug("[AuthContext] logout error", err);
    } finally {
      setUser(null);
      localStorage.removeItem("user");
    }
  };

  const refreshUser = async (force = false) => {
    // allow callers to force an authoritative fetch (useful after OAuth redirect)
    await loadUser(force);
  };

  const value: AuthContextType = {
    user,
    loading,
    // Synchronous check: user presence indicates authenticated state. For authoritative
    // verification call refreshUser()/getCurrentUser().
    isAuthenticated: !!user,
    login,
    logout,
    refreshUser,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};
